<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuth } from '@/stores/auth';
import { NAV_ITEMS, SECTION_LABELS, buildPortalNavItem, type NavSection, type NavItem } from '@/config/navItems';
import IconBrand from '@/components/icons/IconBrand.vue';
import NavBadge from './NavBadge.vue';
import UserMenu from './UserMenu.vue';

defineProps<{ open?: boolean }>();

const auth = useAuth();
const route = useRoute();

const hoaName = computed(() => auth.me?.hoaName ?? '—');
const portalItem = computed(() => buildPortalNavItem(auth.me?.hoaSlug ?? null));

const sections: NavSection[] = ['monitoring', 'configure', 'public'];
const itemsBySection = computed(() => {
  const base: Record<NavSection, NavItem[]> = {
    monitoring: [],
    configure: [],
    public: [],
  };
  for (const item of NAV_ITEMS) base[item.section].push(item);
  if (portalItem.value) base.public.push(portalItem.value);
  return base;
});

/**
 * Returns true when `item` should be considered the active nav link.
 * Path-prefix match (so /issues/123 highlights "Issues"); dashboard is
 * exact-only so it doesn't bleed onto every other route.
 */
function isActive(item: NavItem): boolean {
  const current = route.path ?? '/';
  if (item.id === 'dashboard') return current === item.path;
  return current === item.path || current.startsWith(item.path + '/');
}
</script>

<!--
  AppSidebar — board-side persistent navigation rail.

  Note on visual rules: brief §12 prohibits "left-border accent cards." The
  .nav-item-active::before left-bar marker below is the one accepted exception —
  §12's prohibition targets content cards, not navigation chrome.
-->
<template>
  <aside
    class="bg-surface border-r border-border flex flex-col w-sidebar h-screen
           fixed inset-y-0 left-0 z-50 -translate-x-full transition-transform duration-200
           lg:sticky lg:top-0 lg:translate-x-0"
    :class="{ 'translate-x-0': open }"
    data-testid="app-sidebar"
  >
    <div class="flex items-center gap-2.5 px-4 pt-[18px] pb-4 border-b border-border">
      <span
        class="w-8 h-8 rounded-md grid place-items-center text-white flex-shrink-0"
        style="background: linear-gradient(135deg, var(--color-navy-700), var(--color-navy-900));"
      >
        <IconBrand class="w-[18px] h-[18px]" />
      </span>
      <div class="flex-1 min-w-0">
        <div class="font-semibold text-[15px] -tracking-[0.01em] text-fg truncate">HOA Watch</div>
        <div class="text-[11px] text-muted -mt-px truncate">{{ hoaName }}</div>
      </div>
    </div>

    <nav class="flex-1 overflow-y-auto">
      <div v-for="section in sections" :key="section" class="px-2 py-3">
        <div class="px-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2 mb-1.5">
          {{ SECTION_LABELS[section] }}
        </div>
        <template v-for="item in itemsBySection[section]" :key="item.id">
          <RouterLink
            :to="item.to"
            :target="item.external ? '_blank' : undefined"
            class="nav-item-base"
            :class="{ 'nav-item-active': isActive(item) }"
          >
            <component :is="item.icon" class="w-4 h-4 flex-shrink-0" />
            <span>{{ item.label }}</span>
            <NavBadge v-if="item.badge" :kind="item.badge" />
          </RouterLink>
        </template>
      </div>
    </nav>

    <div class="mt-auto p-3 border-t border-border relative">
      <UserMenu />
    </div>
  </aside>
</template>

<style scoped>
.nav-item-base {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: var(--radius-md);
  color: var(--color-fg-soft);
  font-size: 13.5px;
  font-weight: 500;
  text-decoration: none;
  position: relative;
}
.nav-item-base:hover {
  background: var(--color-navy-50);
  color: var(--color-fg);
}
.nav-item-active {
  background: var(--color-navy-100);
  color: var(--color-navy-800);
  font-weight: 600;
}
.nav-item-active::before {
  content: '';
  position: absolute;
  left: -4px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 2px;
  background: var(--color-navy-700);
}
</style>
